import { useCallback, useEffect, useMemo, useState } from 'react';
import { uploadDownloadManager } from '@/utils/uploadManager';

export type TaskStatus = 'uploading' | 'paused' | 'completed' | 'failed';
export type TaskDirection = 'upload' | 'download';

export interface TaskItem {
  id: string;
  fileName: string;
  domainPath: string;
  fileSize: number;
  transferred: number;
  speed: number;
  status: TaskStatus;
  direction: TaskDirection;
  completedAt?: string;
  error?: string;
  // 断点续传相关
  resumeData?: {
    uploadedBytes?: number;
    downloadedBytes?: number;
    chunks?: Blob[];
    file?: File;
    uploadFile?: File;
    url?: string;
    appUuid?: string;
    objectKey?: string;
    objectUuid?: string;
    dirUuid?: string;
    metadataCategoryUuid?: string;
    hash?: string;
    extension?: string;
    mimeType?: string;
    isSensitive?: number;
    cryptoMode?: string;
    cryptoMeta?: string;
    originalFileSize?: number;
    lifecycleType?: number;
    expireAt?: string;
    description?: string;
  };
}

const STORAGE_KEY = 'task-center-tasks-v1';
const COMPLETED_TASK_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7天
const MAX_COMPLETED_TASKS = 200;

const formatDateTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const parseCompletedAtTimestamp = (value?: string): number => {
  if (!value) return 0;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const ts = new Date(normalized).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const pruneTasks = (tasks: TaskItem[]): TaskItem[] => {
  const now = Date.now();
  const expireBefore = now - COMPLETED_TASK_TTL_MS;
  const activeTasks = tasks.filter(
    (task) => task.status !== 'completed' && task.status !== 'failed',
  );
  const terminalTasks = tasks
    .filter((task) => task.status === 'completed' || task.status === 'failed')
    .filter((task) => {
      const ts = parseCompletedAtTimestamp(task.completedAt);
      // 没有完成时间时保留，避免误删异常历史记录。
      if (!ts) return true;
      return ts >= expireBefore;
    })
    .sort(
      (a, b) =>
        parseCompletedAtTimestamp(b.completedAt) -
        parseCompletedAtTimestamp(a.completedAt),
    )
    .slice(0, MAX_COMPLETED_TASKS);

  return [...activeTasks, ...terminalTasks];
};

const loadCachedTasks = (): TaskItem[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    // 过滤掉进行中的任务（页面刷新后这些任务已失效）
    // 同时清理 resumeData 中无法序列化的对象（File, Blob）
    const cleaned = (parsed as TaskItem[])
      .filter((task) => task.status === 'completed' || task.status === 'failed')
      .map((task) => {
        // 清理 resumeData 中的 File 和 Blob 对象
        if (task.resumeData) {
          const { file, chunks, ...rest } = task.resumeData;
          return {
            ...task,
            resumeData: rest,
          };
        }
        return task;
      });
    return pruneTasks(cleaned);
  } catch (_error) {
    return [];
  }
};

export default () => {
  const [tasks, setTasks] = useState<TaskItem[]>(() => loadCachedTasks());
  const [open, setOpen] = useState(false);

  // 持久化任务到 localStorage
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    // 序列化前清理无法序列化的对象
    const serializableTasks = pruneTasks(tasks).map((task) => {
      if (task.resumeData) {
        const { file, chunks, ...rest } = task.resumeData;
        return {
          ...task,
          resumeData: rest,
        };
      }
      return task;
    });
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(serializableTasks));
  }, [tasks]);

  // 添加新任务
  const addTask = useCallback((task: TaskItem) => {
    setTasks((prev) => pruneTasks([task, ...prev]));
  }, []);

  // 更新任务
  const updateTask = useCallback(
    (id: string, updater: (task: TaskItem) => Partial<TaskItem>) => {
      setTasks((prev) =>
        pruneTasks(
          prev.map((task) =>
            task.id === id ? { ...task, ...updater(task) } : task,
          ),
        ),
      );
    },
    [],
  );

  // 更新任务进度
  const updateTaskProgress = useCallback(
    (id: string, transferred: number, speed: number, total?: number) => {
      updateTask(id, (task) => {
        const updates: Partial<TaskItem> = { transferred, speed };
        // 如果提供了total且当前fileSize为0，更新fileSize
        if (total !== undefined && total > 0 && task.fileSize === 0) {
          updates.fileSize = total;
        }
        return updates;
      });
    },
    [updateTask],
  );

  // 完成任务
  const completeTask = useCallback(
    (id: string) => {
      updateTask(id, (task) => ({
        status: 'completed',
        transferred: task.fileSize,
        speed: 0,
        completedAt: formatDateTime(Date.now()),
      }));
    },
    [updateTask],
  );

  // 任务失败
  const failTask = useCallback(
    (id: string, error: string) => {
      updateTask(id, () => ({
        status: 'failed',
        speed: 0,
        error,
        completedAt: formatDateTime(Date.now()),
      }));
    },
    [updateTask],
  );

  // 暂停任务
  const pauseTask = useCallback(
    (id: string) => {
      updateTask(id, (task) => {
        if (task.status !== 'uploading') {
          return {};
        }
        // 调用 uploadManager 中止请求并保存进度
        const direction = task.direction;
        if (direction === 'upload') {
          const result = uploadDownloadManager.pauseUpload(id);
          if (result) {
            // 保存上传进度
            updateTask(id, () => ({
              resumeData: {
                ...task.resumeData,
                uploadedBytes: result.uploadedBytes,
              },
            }));
          }
        } else {
          const result = uploadDownloadManager.pauseDownload(id);
          if (result) {
            // 保存下载进度
            updateTask(id, () => ({
              resumeData: {
                ...task.resumeData,
                downloadedBytes: result.downloadedBytes,
                chunks: result.chunks,
              },
            }));
          }
        }
        return { status: 'paused', speed: 0 };
      });
    },
    [updateTask],
  );

  // 继续任务（注意：纯前端无法真正恢复，只能重新开始）
  const startTask = useCallback(
    (id: string) => {
      updateTask(id, (task) => {
        if (task.status === 'completed') {
          return {};
        }
        return {
          status: 'uploading',
        };
      });
    },
    [updateTask],
  );

  // 删除任务
  const deleteTask = useCallback(
    (id: string) => {
      // 先中止正在进行的上传/下载
      const task = tasks.find((t) => t.id === id);
      if (task && task.status === 'uploading') {
        if (task.direction === 'upload') {
          uploadDownloadManager.cancelUpload(id);
        } else {
          uploadDownloadManager.cancelDownload(id);
        }
      }
      setTasks((prev) => prev.filter((task) => task.id !== id));
    },
    [tasks],
  );

  // 批量暂停
  const pauseAll = useCallback((direction: TaskDirection) => {
    setTasks((prev) => {
      const tasksToUpdate = prev.filter(
        (task) => task.direction === direction && task.status === 'uploading',
      );

      // 调用 uploadManager 中止所有请求
      tasksToUpdate.forEach((task) => {
        if (direction === 'upload') {
          uploadDownloadManager.pauseUpload(task.id);
        } else {
          uploadDownloadManager.pauseDownload(task.id);
        }
      });

      return prev.map((task) =>
        task.direction === direction && task.status === 'uploading'
          ? { ...task, status: 'paused', speed: 0 }
          : task,
      );
    });
  }, []);

  // 批量继续
  const startAll = useCallback((direction: TaskDirection) => {
    setTasks((prev) =>
      prev.map((task) => {
        if (task.direction !== direction || task.status === 'completed') {
          return task;
        }
        return {
          ...task,
          status: 'uploading',
        };
      }),
    );
  }, []);

  // 批量删除
  const deleteTasks = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        return;
      }
      const idSet = new Set(ids);
      // 先中止所有正在进行的上传/下载
      const tasksToDelete = tasks.filter((t) => idSet.has(t.id));
      tasksToDelete.forEach((task) => {
        if (task.status === 'uploading') {
          if (task.direction === 'upload') {
            uploadDownloadManager.cancelUpload(task.id);
          } else {
            uploadDownloadManager.cancelDownload(task.id);
          }
        }
      });
      setTasks((prev) => prev.filter((task) => !idSet.has(task.id)));
    },
    [tasks],
  );

  const uploadCount = useMemo(
    () => tasks.filter((t) => t.direction === 'upload').length,
    [tasks],
  );
  const downloadCount = useMemo(
    () => tasks.filter((t) => t.direction === 'download').length,
    [tasks],
  );

  return {
    open,
    tasks,
    uploadCount,
    downloadCount,
    show: () => setOpen(true),
    hide: () => setOpen(false),
    addTask,
    updateTask,
    updateTaskProgress,
    completeTask,
    failTask,
    pauseTask,
    startTask,
    deleteTask,
    pauseAll,
    startAll,
    deleteTasks,
    setTasks,
  };
};
